import { IElementWaypoint } from "@/hooks/use-placeholders";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { selectActiveElement } from "@/store/selections-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  isSingleScanSection,
  selectAncestor,
  selectIsLocationMarkerSection,
} from "@faro-lotv/app-component-toolbox";
import { isIElementRoomSection } from "@faro-lotv/ielement-types";
import { createSelector } from "@reduxjs/toolkit";
import { useCallback } from "react";
import { SheetWaypointsProps } from "./sheet-waypoints";

/** Tooltip message given to waypoints, that can be moved after being activated. */
const CLICK_TO_ACTIVATE_REASON = "Click to activate this waypoint";

/**
 * @returns Functions to compute the allowed waypoints interactions for waypoints.
 * @param isInMinimap Whether the interactions are computed for the minimap.
 * @param isWaypointMoveActive Whether the waypoint moving is active.
 */
export function useComputeAllowedWaypointInteractions(
  isInMinimap: boolean,
  isWaypointMoveActive: boolean,
): Pick<
  SheetWaypointsProps,
  "isPlaceholderDisabledForMove" | "isPlaceholderDisabledForClick"
> {
  const iElements = useAppSelector(selectIElementsSlice);
  const activeElement = useAppSelector(selectActiveElement);

  // Function to compute on demand, whether a placeholder is allowed to be moved.
  const isPlaceholderDisabledForMove = useCallback(
    (el: IElementWaypoint) => {
      const waypointDisabledReason = selectIsWaypointDisabledForMove(el)({
        iElements,
      });
      if (waypointDisabledReason) return waypointDisabledReason;

      // In the minimap, only the active element is allowed to be moved
      if (isInMinimap) {
        if (
          !selectAncestor(
            activeElement,
            (ancestor) => ancestor.id === el.id,
          )({ iElements })
        ) {
          if (selectIsLocationMarkerSection(el.id)({ iElements })) {
            return "Move location markers in the 2D overview";
          }

          // In the minimap, moving is only allowed for the active element
          return CLICK_TO_ACTIVATE_REASON;
        }
      }

      return false;
    },
    [activeElement, iElements, isInMinimap],
  );

  // Function to compute on demand, whether a placeholder is allowed to be clicked.
  // When the waypoint moving is enabled, the clicking is generally disabled, unless we are in the minimap, and a waypoint can be moved.
  const isPlaceholderDisabledForClick = useCallback(
    (el: IElementWaypoint) =>
      isWaypointMoveActive &&
      !(
        isInMinimap &&
        // Only allow activating, when the isPlaceholderDisabledForMove determined that it can be moved after clicking
        isPlaceholderDisabledForMove(el) === CLICK_TO_ACTIVATE_REASON
      ),
    [isInMinimap, isPlaceholderDisabledForMove, isWaypointMoveActive],
  );

  return {
    isPlaceholderDisabledForMove,
    isPlaceholderDisabledForClick,
  };
}

/**
 * @returns selector to determine whether a waypoint can be moved. Returns a reason string if not allowed, or undefined if it can be moved.
 * @param state App state
 * @param waypoint The waypoint to check
 */
export const selectIsWaypointDisabledForMove = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, waypoint?: IElementWaypoint) => waypoint,
    ],
    (iElements, waypoint) => {
      // Only allows Rooms to be moved for now. Other data has its position defined through other means.
      // (e.g. registration or video mode alignment)
      if (!selectAncestor(waypoint, isIElementRoomSection)({ iElements })) {
        if (selectAncestor(waypoint, isSingleScanSection)({ iElements })) {
          return "Scan waypoints cannot be moved";
        }
        return "This waypoint cannot be moved";
      }
    },
  ),
);
